import type { Sound } from "../../engine/things/sounds/sound";
import type { SoundManager } from "./soundManager";

/**
 * Holds sounds that opted into animation-ordered playback (Sound.timing != null).
 * Sounds are released into SoundManager only when their animationOrder is reached,
 * so audio stays in sync with the EffectManager's order progression rather than
 * firing immediately at turn start.
 *
 * Sounds are removed from the queue on release — already-released sounds are not
 * revisited when later releaseUpTo calls advance the order.
 */
export class SoundQueue {
	private pending: Sound[] = [];

	load(sounds: Sound[]): void {
		this.pending.push(...sounds);
		this.pending.sort((a, b) => a.getAnimationOrder() - b.getAnimationOrder());
	}

	releaseUpTo(currentOrder: number | null, manager: SoundManager): void {
		if (currentOrder === null) return;
		while (this.pending.length > 0 && this.pending[0].getAnimationOrder() <= currentOrder) {
			const sound = this.pending.shift()!;
			manager.manageSound(sound);
		}
	}

	releaseAll(manager: SoundManager): void {
		while (this.pending.length > 0) {
			manager.manageSound(this.pending.shift()!);
		}
	}

	clear(): void {
		this.pending.length = 0;
	}

	isEmpty(): boolean {
		return this.pending.length === 0;
	}
}
